import React, { useState, useEffect } from 'react'
import './Blog.css'
import BlogSubscribeForm from '../SubscribeFormFolder/BlogSubscribeForm'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Auth';

import {
  LazyLoadImage,
  LazyLoadComponent,
} from 'react-lazy-load-image-component'

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { CiShare2 } from "react-icons/ci"

// Assets
const blogBanner = require('./img/blog-banner.png')

// Interface for reading image
interface Blogs {
  postID: number;
  link_name: string;
  title: string;
  author: string;
  summary: string;
  tags: string;
  date: string;   // string because it comes from API
  mainImage: any;
  draft: boolean;
}

export function displayNotification() {
  toast.success("Link copied to your clipboard!", {
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    style: {
      backgroundColor: '#c3c9cfff',
      color: 'black',
      fontSize: '16px',
    }
  });
}

const BlogCard = ({
  postID,
  link_name,
  image,
  tags,
  title,
  author,
  date,
  content,
  displayNotification,
}: {
  postID: number;
  link_name: string;
  image: string;
  tags: string[];
  title: string;
  author: string;
  date: string;
  content: string;
  displayNotification: () => void;
}) => {
  const navigate = useNavigate();

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = link_name
      ? `${window.location.origin}/fountainn/blog/${link_name}`
      : `${window.location.origin}/fountainn/post/${postID}`;

    navigator.clipboard.writeText(url)
      .then(() => displayNotification())
      .catch(err => console.error("Failed to copy: ", err));
  };

  return (
    <div
      className="blog-card"
      onClick={() => navigate(
        link_name
          ? `/fountainn/blog/${link_name}`
          : `/fountainn/post/${postID}`
      )}
    >
      <div className='blog-card-img-with-tags'>
        <img src={image} alt="Blog" className="blog-card-img" />
        <div className="dark-overlay"></div>
        <div className='blog-tags'>
          {tags.map((tag, i) => (
            <div className='blog-tag' key={i}>{tag}</div>
          ))}
        </div>
        <div className="blog-share" onClick={handleShareClick}>
          <CiShare2 className='share-icon' />
        </div>
      </div>

      <div className='blog-card-title-and-content'>
        <h1>{title}</h1>
        <div className='blog-credentials'>
          <p style={{ color: '#000' }}>{author}</p>
          <p>━</p>
          <p className="blog-date">{date}</p>
        </div>
        <p style={{ color: '#6C757D' }}>{content}</p>
      </div>
    </div>
  );
};

const Blog = () => {
  const [blogs, setBlogs] = useState<Blogs[]>([]);
  const { isAuthenticated, getPermissions } = useAuth();
  const [permissionGroups, setPermissionGroups] = useState<{ [key: string]: any }>({});
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      const permissions = await getPermissions();
      setPermissionGroups(permissions || {});
    };

    if (isAuthenticated) {
      fetchPermissions();
    }
  }, [isAuthenticated, getPermissions]);

  // Fetch blog posts

  const API_BASE = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000'
    : '';

  const fetchBlogs = async (url: string, isInitial = false) => {
    try {
      setLoading(true);

      const response = await fetch(url, {
        method: 'GET',
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();

      if (isInitial) {
        setBlogs(data.results);
      } else {
        setBlogs(prev => [...prev, ...data.results]);
      }

      // Extract only the path to avoid http:// vs https:// mismatch
      if (data.next) {
        const nextUrl = new URL(data.next);
        setNextPage(API_BASE + nextUrl.pathname + nextUrl.search);
      } else {
        setNextPage(null);
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(`${API_BASE}/api/blogposts/`, true);
  }, []);


  return (
    <div className="home-container">
      <ToastContainer />

      <div className="blog-hero-section">
        <LazyLoadComponent placeholder={<img src={blogBanner} alt="" />}>
          <LazyLoadImage src={blogBanner} alt="Banner" className="hero-image3" />
        </LazyLoadComponent>
        <div className="dark-overlay"></div>
        <div className="news-info-wrapper">
          <h1>FountaiNN</h1>
        </div>
      </div>

      <div className='blog-hero-section2'>
        <p>A resource hub offering diverse information on FNN™’s core topics. Here, we share a wide range of
          insightful blog posts that dive into key areas related to FNN™’s mission, technologies, and community.</p>
      </div>

      <div className='blog-card-wrapper'>
        <h1>Explore the FountaiNN</h1>
        <div className='blog-card-stack'>
          {loading && blogs.length === 0
            ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="blog-card blog-card-skeleton">
                <div className="blog-card-img-with-tags">
                  <div className="blog-skeleton blog-skeleton-image" />
                </div>

                <div className="blog-card-title-and-content">
                  <div className="blog-skeleton blog-skeleton-title" />
                  <div className="blog-skeleton blog-skeleton-credentials" />
                  <div className="blog-skeleton blog-skeleton-text" />
                  <div className="blog-skeleton blog-skeleton-text short" />
                </div>
              </div>
            ))
            : blogs.map(blog => (
              <BlogCard
                key={blog.postID}
                postID={blog.postID}
                link_name={blog.link_name}
                image={blog.mainImage}
                tags={blog.tags.split(',').map(tag => tag.trim())}
                title={blog.title}
                author={blog.author}
                date={blog.date ? new Date(blog.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : ""}
                content={blog.summary}
                displayNotification={displayNotification}
              />
            ))}
        </div>

        {/* Show Load More button only if there are more than 6 posts */}
        {nextPage && (
          <button
            className='load-more-blogs-button'
            onClick={() => fetchBlogs(nextPage)}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        )}
      </div>

      <BlogSubscribeForm />
    </div>
  )
}


export default Blog;
